import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Flame } from "lucide-react";
import axios from "axios";
import { SEO } from "@/components/SEO";
import { HoverImage } from "@/components/HoverImage";

const API = process.env.REACT_APP_BACKEND_URL;
const CLEARANCE_HERO = "/products/clearance/731077_076de3e8fe1f4774923bb881e334e6d6~mv2.jpg";
const fmt = (n) => n.toLocaleString("en-NZ", { minimumFractionDigits: 0, maximumFractionDigits: 2 });

// Already-native products elsewhere on the site, currently at a clearance/sale price —
// cross-linked here, not duplicated. Their own detail page already shows the sale price/badge.
const CLEARANCE_LINKED_PRODUCTS = [
  { name: "Quick Pitch Aero — Lightweight Hard Shell Roof Tent", price: 3890, priceWas: 4790, badge: "Clearance", thumb: "/products/rooftents/731077_9f6dcff029df4808b3ea745996181dd8~mv2.jpg", to: "/roof-tents/quick-pitch-aero" },
];

const Card = ({ item, testid }) => {
  const savingPct = Math.round(((item.priceWas - item.price) / item.priceWas) * 100);
  return (
    <Link
      to={item.to}
      data-testid={testid}
      className="group relative block overflow-hidden border border-[var(--brand-primary)]/40 hover:border-[var(--brand-primary)] transition-colors bg-[var(--bg-secondary)]"
    >
      <div className="aspect-[4/3] overflow-hidden relative">
        <HoverImage primary={item.thumb} secondary={item.gallery?.[1]?.src} alt={item.name} />
        <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/40 to-transparent" />
        <span className="absolute top-4 left-4 font-mono text-[10px] tracking-[0.25em] uppercase text-black bg-[var(--brand-primary)] px-2.5 py-1">
          {item.badge}
        </span>
        {!Number.isNaN(savingPct) && (
          <span className="absolute top-4 right-4 font-mono text-[10px] tracking-[0.2em] uppercase text-white bg-black/70 border border-[var(--brand-primary)]/60 px-2.5 py-1">
            Save {savingPct}%
          </span>
        )}
        <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6">
          <h3 className="font-display text-lg md:text-xl text-white leading-tight tracking-wide">{item.name.toUpperCase()}</h3>
          <div className="flex items-end justify-between gap-3 mt-4">
            <div>
              <p className="font-mono text-[11px] text-white/40 line-through">${fmt(item.priceWas)}</p>
              <p className="font-display text-2xl text-[var(--brand-primary)] leading-none">${fmt(item.price)}</p>
            </div>
            <span className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.2em] text-white group-hover:text-[var(--brand-primary)] transition-colors">
              View <ArrowUpRight size={12} />
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
};

export default function ClearancePage() {
  const [products, setProducts] = useState(null); // null = still loading

  useEffect(() => {
    axios.get(`${API}/api/products/clearance`).then((res) => setProducts(res.data)).catch((err) => { console.error(err); setProducts([]); });
  }, []);

  if (products === null) {
    return <div className="min-h-[60vh] flex items-center justify-center text-white/50 font-mono text-sm">Loading…</div>;
  }

  const nativeItems = products.map((p) => ({ ...p, to: `/clearance/${p.slug}` }));
  const allItems = [...nativeItems, ...CLEARANCE_LINKED_PRODUCTS].sort((a, b) => b.price - a.price);

  return (
    <div data-testid="clearance-page">
      <SEO
        path="/clearance"
        title="Clearance & Specials NZ · XpeditioNZ"
        description="Ex-display and clearance-priced overland gear — one-off savings across our full range of genuine brands while stock lasts."
        image={CLEARANCE_HERO}
      />

      <section className="relative min-h-[55svh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img src={CLEARANCE_HERO} alt="Clearance and ex-display overland gear" className="w-full h-full object-cover" style={{ objectPosition: "50% 40%" }} />
          <div className="absolute inset-0 bg-gradient-to-t from-[var(--bg-main)] from-10% via-black/30 via-45% to-transparent" />
        </div>
        <div className="relative z-10 container-x pb-14 pt-32 w-full">
          <p className="overline mb-6 flex items-center gap-2">
            <Flame size={14} className="text-[var(--brand-primary)]" /> — Clearance & Specials
          </p>
          <h1 className="font-display text-[10vw] md:text-[7vw] lg:text-[5.5vw] leading-[0.9] text-white hero-text-shadow max-w-[18ch]">
            LIMITED STOCK.<br />
            <span className="text-[var(--brand-primary)]">SERIOUS SAVINGS.</span>
          </h1>
          <p className="text-base md:text-lg font-medium md:font-normal text-white/95 md:text-white/85 max-w-xl leading-relaxed mt-8 [text-shadow:0_1px_3px_rgba(0,0,0,0.9),0_2px_20px_rgba(0,0,0,0.7)] md:[text-shadow:none]">
            Ex-display units and one-off clearance pricing across our full range of genuine gear. Once it's gone, it's gone — no reorders on these prices.
          </p>
        </div>
      </section>

      <section className="container-x py-16 md:py-20">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="clearance-grid">
          {allItems.map((item, i) => <Card key={item.to} item={item} testid={`clearance-item-${i}`} />)}
        </div>
      </section>
    </div>
  );
}
